import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, Scissors, Play, Pause, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-[#1E2A5A] border border-white/10 text-white text-sm focus:border-[#E6B34A] focus:outline-none";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Encode an AudioBuffer (optionally a slice) to a 16-bit PCM WAV Blob.
function encodeWav(buffer) {
  const numCh = buffer.numberOfChannels;
  const total = buffer.length * numCh * 2 + 44;
  const ab = new ArrayBuffer(total);
  const view = new DataView(ab);
  const rate = buffer.sampleRate;
  let offset = 0;
  const writeStr = (s) => { for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i)); };
  writeStr("RIFF"); view.setUint32(offset, total - 8, true); offset += 4;
  writeStr("WAVE"); writeStr("fmt "); view.setUint32(offset, 16, true); offset += 4;
  view.setUint16(offset, 1, true); offset += 2;
  view.setUint16(offset, numCh, true); offset += 2;
  view.setUint32(offset, rate, true); offset += 4;
  view.setUint32(offset, rate * numCh * 2, true); offset += 4;
  view.setUint16(offset, numCh * 2, true); offset += 2;
  view.setUint16(offset, 16, true); offset += 2;
  writeStr("data"); view.setUint32(offset, buffer.length * numCh * 2, true); offset += 4;
  const channels = [];
  for (let c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));
  for (let i = 0; i < buffer.length; i++) {
    for (let c = 0; c < numCh; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true); offset += 2;
    }
  }
  return new Blob([ab], { type: "audio/wav" });
}

function sliceBuffer(ctx, buffer, start, end) {
  const rate = buffer.sampleRate;
  const s = Math.floor(start * rate);
  const e = Math.min(buffer.length, Math.floor(end * rate));
  const out = ctx.createBuffer(buffer.numberOfChannels, Math.max(1, e - s), rate);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    out.copyToChannel(buffer.getChannelData(ch).subarray(s, e), ch, 0);
  }
  return out;
}

// Review dialog shown right after a broadcast ends: name, describe, trim, then publish.
export default function RecordingReview({ blob, defaultTitle, tracks = [], onClose, onPublished }) {
  const [title, setTitle] = useState(defaultTitle || "Live Broadcast");
  const [description, setDescription] = useState("");
  const [series, setSeries] = useState("Live Broadcasts");
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [decoding, setDecoding] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const bufferRef = useRef(null);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const audioUrl = useRef(URL.createObjectURL(blob));

  // Draw the waveform + trim selection whenever the buffer or selection changes
  useEffect(() => {
    const canvas = canvasRef.current;
    const buf = bufferRef.current;
    if (!canvas || !buf) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    canvas.width = w * dpr; canvas.height = h * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    const data = buf.getChannelData(0);
    const step = Math.max(1, Math.floor(data.length / w));
    const mid = h / 2;
    const selStart = (start / duration) * w;
    const selEnd = (end / duration) * w;
    // dimmed background waveform
    for (let x = 0; x < w; x++) {
      let min = 1, max = -1;
      for (let j = 0; j < step; j++) {
        const v = data[x * step + j] || 0;
        if (v < min) min = v; if (v > max) max = v;
      }
      const inSel = x >= selStart && x <= selEnd;
      ctx.strokeStyle = inSel ? "#E6B34A" : "rgba(255,255,255,0.15)";
      ctx.beginPath();
      ctx.moveTo(x + 0.5, mid + min * mid * 0.9);
      ctx.lineTo(x + 0.5, mid + max * mid * 0.9);
      ctx.stroke();
    }
    // selection edges
    ctx.strokeStyle = "#E6B34A";
    ctx.lineWidth = 2;
    [selStart, selEnd].forEach((x) => { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); });
  }, [decoding, start, end, duration]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
        if (cancelled) return;
        bufferRef.current = buf;
        setDuration(buf.duration);
        setEnd(buf.duration);
      } catch (e) {
        // Decoding unsupported: keep original blob, disable trim
        bufferRef.current = null;
      } finally {
        if (!cancelled) setDecoding(false);
      }
    })();
    const url = audioUrl.current;
    return () => { cancelled = true; URL.revokeObjectURL(url); };
  }, [blob]);

  const trimmed = bufferRef.current && (start > 0.1 || end < duration - 0.1);

  const playSelection = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); return; }
    el.currentTime = start;
    el.play();
    setPlaying(true);
    const onTime = () => { if (el.currentTime >= end) { el.pause(); setPlaying(false); el.removeEventListener("timeupdate", onTime); } };
    el.addEventListener("timeupdate", onTime);
    el.onended = () => setPlaying(false);
  };

  const publish = async () => {
    if (!title.trim()) { toast.error("Please give the recording a title"); return; }
    setPublishing(true);
    try {
      let fileBlob = blob;
      let filename = `broadcast-${Date.now()}.webm`;
      if (trimmed && bufferRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const seg = sliceBuffer(ctx, bufferRef.current, start, end);
        fileBlob = encodeWav(seg);
        filename = `broadcast-${Date.now()}.wav`;
      }
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("series", series.trim() || "Live Broadcasts");
      if (tracks && tracks.length) {
        const offset = (trimmed && bufferRef.current) ? start : 0;
        const maxAt = (trimmed && bufferRef.current) ? (end - start) : Infinity;
        const adj = tracks.map((t) => ({ ...t, at: Math.max(0, Math.round((t.at || 0) - offset)) })).filter((t) => t.at >= 0 && t.at <= maxAt);
        if (adj.length) fd.append("tracks", JSON.stringify(adj));
      }
      fd.append("file", fileBlob, filename);
      await api.post("/episodes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Recording published to Episodes 🎙️");
      onPublished && onPublished();
    } catch (e) {
      toast.error("Could not publish the recording");
    } finally { setPublishing(false); }
  };

  const discard = () => {
    if (window.confirm("Discard this recording? It will not be saved.")) onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="recording-review">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border border-[#E6B34A]/20 bg-[#141C42] p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-xl flex items-center gap-2"><Save className="w-5 h-5 text-[#E6B34A]" /> Review your recording</h3>
          <button onClick={discard} data-testid="rec-discard-x" className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-5">Name it, trim the start/end if needed, then publish it to Episodes.</p>

        <audio ref={audioRef} src={audioUrl.current} className="hidden" />

        <div className="space-y-4">
          <div>
            <label className="overline text-zinc-400">Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="rec-title" className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="overline text-zinc-400">Series</label>
            <input value={series} onChange={(e) => setSeries(e.target.value)} data-testid="rec-series" className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="overline text-zinc-400">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="rec-desc" className={`${inputCls} mt-1.5`} />
          </div>

          {tracks && tracks.length > 0 && (
            <div className="rounded-2xl border border-white/5 bg-[#0C1338] p-4" data-testid="rec-tracks">
              <p className="overline text-zinc-400 mb-2">Tracks aired ({tracks.length}) — saved with the replay</p>
              <div className="space-y-1 max-h-32 overflow-auto pr-1">
                {tracks.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${t.kind === "sermon" ? "bg-blue-500/15 text-blue-300" : "bg-[#E6B34A]/15 text-[#E6B34A]"}`}>{t.kind === "sermon" ? "Sermon" : "Song"}</span>
                    <span className="truncate">{t.title}{t.artist ? ` — ${t.artist}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trim */}
          <div className="rounded-2xl border border-white/5 bg-[#0C1338] p-4" data-testid="rec-trim">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#E6B34A]"><Scissors className="w-4 h-4" /> Trim</span>
              <button type="button" onClick={playSelection} disabled={decoding} data-testid="rec-preview" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold transition-colors disabled:opacity-50">
                {playing ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Preview selection</>}
              </button>
            </div>
            {decoding ? (
              <div className="flex items-center gap-2 text-sm text-zinc-500 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Preparing audio…</div>
            ) : !bufferRef.current ? (
              <p className="text-xs text-zinc-500">Trimming isn't supported in this browser — the full recording will be published.</p>
            ) : (
              <div className="space-y-3">
                <canvas ref={canvasRef} data-testid="rec-waveform" className="w-full h-20 rounded-lg bg-[#0A1030]/60" />
                <div>
                  <div className="flex justify-between text-xs text-zinc-400 mb-1"><span>Start</span><span className="tabular-nums">{fmt(start)}</span></div>
                  <input type="range" min={0} max={duration} step={0.1} value={start} onChange={(e) => setStart(Math.min(parseFloat(e.target.value), end - 0.5))} data-testid="rec-trim-start" className="w-full accent-[#E6B34A]" />
                </div>
                <div>
                  <div className="flex justify-between text-xs text-zinc-400 mb-1"><span>End</span><span className="tabular-nums">{fmt(end)}</span></div>
                  <input type="range" min={0} max={duration} step={0.1} value={end} onChange={(e) => setEnd(Math.max(parseFloat(e.target.value), start + 0.5))} data-testid="rec-trim-end" className="w-full accent-[#E6B34A]" />
                </div>
                <p className="text-xs text-zinc-500">Final length: <span className="text-zinc-300 tabular-nums">{fmt(Math.max(0, end - start))}</span>{trimmed ? " (trimmed)" : " (full)"}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={publish} disabled={publishing || decoding} data-testid="rec-publish" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
            {publishing ? <><Loader2 className="w-5 h-5 animate-spin" /> Publishing…</> : "Publish to Episodes"}
          </button>
          <button onClick={discard} disabled={publishing} data-testid="rec-discard" className="px-5 py-3 rounded-xl bg-white/5 text-zinc-300 hover:bg-[#FF3B30]/10 hover:text-[#FF7A70] font-semibold transition-colors">Discard</button>
        </div>
      </div>
    </div>
  );
}
